import { create } from 'zustand';
import useVoyagerStore from '../stores/useVoyagerStore';
import type { LandingAgentStatus, LandingAgentTurn, ParkedConversation } from '../stores/slices/landingAgentSlice';

/**
 * The simplified app's one sidebar (#1503), its own state: hidden or shown on a
 * wide screen, remembered per browser under its own key, and the page a
 * destination without its own page yet
 * shows (Action items, Agents, Impact: their tickets build them).
 */
export const SIMPLE_SIDEBAR_KEY = 'stroma-simple-sidebar';

export type SimplePlaceholder = 'action-items' | 'agents' | 'impact';

const remembered = (): boolean => {
  try { return window.localStorage.getItem(SIMPLE_SIDEBAR_KEY) === 'hidden'; } catch { return false; }
};

interface SimpleSidebarState {
  collapsed: boolean;
  placeholder: SimplePlaceholder | null;
  /** The current thread finished while the asker was on another page: "done" until they come back to it. */
  awayDone: string | null;
  setCollapsed: (collapsed: boolean) => void;
  showPlaceholder: (page: SimplePlaceholder | null) => void;
}

export const useSimpleSidebar = create<SimpleSidebarState>((set) => ({
  collapsed: remembered(),
  placeholder: null,
  awayDone: null,
  setCollapsed: (collapsed) => {
    try { window.localStorage.setItem(SIMPLE_SIDEBAR_KEY, collapsed ? 'hidden' : 'shown'); } catch { /* the choice still holds for this page */ }
    set({ collapsed });
  },
  showPlaceholder: (placeholder) => set({ placeholder }),
}));

// Navigation elsewhere leaves a placeholder page: another view, an objective's space, another thread.
// A run still writing to the conversation under it, or the thread id a first answer mints, does not
// (the asker went to the placeholder; their answer must not pull them back). Back and Forward are
// SimpleShell's (popstate); the sidebar, New chat and the toast's Open clear it themselves.
useVoyagerStore.subscribe((s, prev) => {
  if (!useSimpleSidebar.getState().placeholder) return;
  if (s.currentView !== prev.currentView || s.objectiveSpaceId !== prev.objectiveSpaceId
    || (prev.currentThreadId !== null && s.currentThreadId !== prev.currentThreadId)) {
    useSimpleSidebar.setState({ placeholder: null });
  }
});

/** A Recents row's marker, as the mock draws it: waiting on the asker, running, finished while away, or failed while away. */
export type RecentMarker = 'needs' | 'run' | 'done' | 'fail' | null;

/**
 * THE needs-you rule (#1503, owner rule recorded on the ticket): a thread needs you exactly when its LATEST turn
 * waits on the asker — a clarification it is asking, a definition card, or a reading card — and that wait has not
 * been resolved. A wait ends when the asker acts on the card in any way it offers: an action that asks again
 * (answer, skip, a route or scope option, see results with a draft, use an existing metric, answer without it, a
 * reading choice) makes a newer turn the latest; one that does not (send for review, a saved definition, a reading
 * choice) is recorded by the store's one `resolveWait` on the turn itself (`waitResolved`, kept on the thread's snapshot). Earlier turns never make a thread need you.
 */
export function needsYou(turns: ReadonlyArray<LandingAgentTurn> | undefined): boolean {
  const latest = turns && turns.length ? turns[turns.length - 1] : undefined;
  if (!latest || latest.waitResolved) return false;
  // a turn the titles-only list carries without its answer (#1596): the server read it by this same rule
  if (latest.answerPending) return latest.waiting === true;
  return (latest.status === 'clarifying' && !!latest.clarification) || !!latest.definitionRequest || !!latest.readingConfirmation;
}

type Conversation = { status: LandingAgentStatus; turns: ReadonlyArray<LandingAgentTurn> };
type MarkerState = {
  parkedConversations: Record<string, ParkedConversation>;
  currentThreadId: string | null;
  landingAgentStatus: LandingAgentStatus;
  landingAgentTurns: ReadonlyArray<LandingAgentTurn>;
  currentView: string;
};

/** The conversation on screen is on the landing view with no placeholder page over it. */
export function onScreen(s: Pick<MarkerState, 'currentView'>, placeholder: SimplePlaceholder | null): boolean {
  return s.currentView === 'landing' && !placeholder;
}

/**
 * From the store's conversations (no new server state): a parked thread from its
 * parked copy; the current thread from the live one, and — when the asker went to
 * another page while it ran — "done" from `awayDone` until they come back to it.
 * Opening a thread unparks it, which clears its marker.
 */
export function recentMarker(s: MarkerState, threadId: string, awayDone: string | null): RecentMarker {
  const current = s.currentThreadId === threadId;
  const parked = s.parkedConversations[threadId];
  const conv: Conversation | null = current ? { status: s.landingAgentStatus, turns: s.landingAgentTurns }
    : parked ? { status: parked.status, turns: parked.turns } : null;
  if (!conv) return null;
  if (needsYou(conv.turns)) return 'needs';
  if (conv.status === 'running') return 'run';
  // a run the asker stopped is not news
  if (conv.status === 'cancelled') return null;
  if (current ? awayDone === threadId : parked?.unread) return conv.status === 'error' ? 'fail' : 'done';
  return null;
}

/** The hidden sidebar's toggle carries a dot while any thread not on screen has a marker. */
export function hasNews(s: MarkerState, placeholder: SimplePlaceholder | null, awayDone: string | null): boolean {
  if (Object.keys(s.parkedConversations).some((id) => recentMarker(s, id, awayDone) !== null)) return true;
  return !!s.currentThreadId && !onScreen(s, placeholder) && recentMarker(s, s.currentThreadId, awayDone) !== null;
}

/** "Finished elsewhere" (#1503): a thread not on screen got its answer, or now waits on the asker. */
export type FinishedToast = { threadId: string; generation: number };

export const useFinishedToast = create<{ toast: FinishedToast | null }>(() => ({ toast: null }));

/** The threads that turned unread between two `parkedConversations`: each one finished while away. */
export function newlyFinished(prev: Record<string, ParkedConversation>, next: Record<string, ParkedConversation>): string[] {
  return Object.keys(next).filter((id) => next[id].unread && !prev[id]?.unread && next[id].status !== 'cancelled');
}
